/**
 * PermissionsMatrixPage.tsx — Matriz de permisos por rol + overrides por usuario
 * Solo accesible para ADMIN y SUPER_ADMIN.
 *
 * Features:
 * - Matriz por rol (lectura)
 * - Overrides por usuario (editable)
 * - Organizado por módulos
 */

import { useState } from 'react';
import {
  Text, makeStyles, tokens, Button, Input, Spinner,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Badge, useToastController, Toast, ToastTitle,
} from '@fluentui/react-components';
import {
  ShieldCheckmark24Regular, CheckmarkCircle24Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { usePermission } from '../hooks/usePermission';
import type { Permission } from '../api/permissions.api';
import { getPermissionsMatrix, updateRoleAdjustments } from '../api/permissions.api';
import { useAuthStore } from '../store/auth.store';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { getUserPermissionOverrides, updateUserPermissionOverrides } from '../api/permissions.api';
import { fetchUsers } from '../api/settings.api';
import { PageTitle } from '../components/shared/PageTitle';
import { SubTabs } from '../components/shared/SubTabs';

// ── Datos de la matriz ────────────────────────────────────────────────────────

const ROLES = ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'WAREHOUSE_MANAGER', 'AUDITOR', 'INVESTOR', 'HR', 'GUARD', 'ADMIN', 'SUPER_ADMIN'] as const;

// Module keys for i18n lookup
const PERMISSION_MODULES_KEYS = {
  GESTIÓN: [
    'users:read', 'users:write',
    'admin:ranchos', 'admin:rbac', 'admin:super',
  ],
  OPERACIONES: [
    'inventory:read', 'inventory:write',
    'payroll:read', 'payroll:write',
    'machinery:read',
  ],
  CAMPO: [
    'lotes:read', 'lotes:write',
    'tasks:read', 'tasks:write', 'tasks:manage',
    'crop:write',
    'irrigation:write',
  ],
  ASISTENCIA: [
    'attendance:read', 'attendance:take', 'attendance:enroll', 'attendance:policy',
    'attendance:prebaja_decide', 'attendance:delete_biometrics', 'attendance:gate', 'attendance:gate_authorize',
  ],
  COSECHA: [
    'harvest:read', 'harvest:write',
    'merma:read',
  ],
  ANÁLISIS: [
    'finance:read', 'finance:write',
    'reports:read',
    'audit:read',
  ],
  SISTEMA: [
    'vocabulary:write',
    'config:write',
  ],
} as const;

type Module = keyof typeof PERMISSION_MODULES_KEYS;

// ── Estilos ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    padding: '24px',
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    marginBottom: '24px',
    display: 'block',
  },
  tabs: {
    marginBottom: '20px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  moduleSection: {
    marginBottom: '32px',
  },
  moduleTitle: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '12px',
    color: tokens.colorBrandForeground1,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    marginBottom: '16px',
  },
  headerCell: {
    fontWeight: '600',
    fontSize: '12px',
    textAlign: 'center' as const,
    whiteSpace: 'normal' as const,
    lineHeight: '1.2',
    padding: '6px 4px',
  },
  permCell: {
    minWidth: '260px',
    width: '280px',
    fontWeight: '500',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  checkCell: {
    textAlign: 'center' as const,
    cursor: 'pointer',
  },
  allowed: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: '700',
    fontSize: '16px',
  },
  denied: {
    color: tokens.colorNeutralForeground4,
    fontSize: '16px',
  },
  overrideSearchWrap: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    marginBottom: '24px',
  },
  noAccess: {
    padding: '32px',
    color: tokens.colorNeutralForeground3,
  },
});

// ── Componente ────────────────────────────────────────────────────────────────

export function PermissionsMatrixPage() {
  const s = useStyles();
  const t = useT();
  const { dispatchToast } = useToastController('agrovid-toaster');
  const canManageRoles = usePermission('admin:rbac');
  // F0.3: la matriz la sirve el backend desde rbac.model.ts — el frontend ya no
  // mantiene su propia copia (se desincronizaba del API).
  const { data: matrix } = useQuery({ queryKey: ['permissions-matrix'], queryFn: getPermissionsMatrix });
  const qc = useQueryClient();
  const isSuperAdmin = !!useAuthStore((st) => st.user?.roles?.includes('SUPER_ADMIN'));
  // Borrador de la matriz por rol (solo Super Admin): null = sin cambios pendientes.
  const [draft, setDraft] = useState<Record<string, Permission[]> | null>(null);
  const [savingRoles, setSavingRoles] = useState(false);
  const rolePermissions: Record<string, Permission[]> = draft ?? matrix?.roles ?? ({} as any);
  const nonAdjustable = matrix?.nonAdjustableRoles ?? ['SUPER_ADMIN'];
  const nonGrantable = matrix?.nonGrantable ?? ['admin:super', 'admin:rbac'];
  const canEditCell = (role: string, perm: string) =>
    isSuperAdmin && !!matrix?.defaults && !nonAdjustable.includes(role)
    && (matrix.defaults[role as keyof typeof matrix.defaults] ?? []).length >= 0
    && !(nonGrantable.includes(perm as Permission) && !(matrix.defaults[role as keyof typeof matrix.defaults] ?? []).includes(perm as Permission));
  const toggleCell = (role: string, perm: Permission) => {
    if (!matrix || !canEditCell(role, perm)) return;
    const base = draft ?? (matrix.roles as Record<string, Permission[]>);
    const cur = base[role] ?? [];
    const next = cur.includes(perm) ? cur.filter((p) => p !== perm) : [...cur, perm];
    setDraft({ ...Object.fromEntries(Object.entries(base).map(([r, ps]) => [r, [...ps]])), [role]: next });
  };
  const saveRoles = async () => {
    if (!matrix?.defaults || !draft) return;
    setSavingRoles(true);
    try {
      const adjustments: Record<string, { add: Permission[]; remove: Permission[] }> = {};
      for (const role of Object.keys(draft)) {
        if (nonAdjustable.includes(role)) continue;
        const base = new Set<Permission>(matrix.defaults[role as keyof typeof matrix.defaults] ?? []);
        const now = new Set<Permission>(draft[role]);
        const add = [...now].filter((p) => !base.has(p));
        const remove = [...base].filter((p) => !now.has(p));
        if (add.length || remove.length) adjustments[role] = { add, remove };
      }
      await updateRoleAdjustments(adjustments);
      await qc.invalidateQueries({ queryKey: ['permissions-matrix'] });
      setDraft(null);
      dispatchToast(<Toast><ToastTitle>{t.permissionsMatrix.rolesSaved}</ToastTitle></Toast>, { intent: 'success' });
    } catch {
      dispatchToast(<Toast><ToastTitle>{t.permissionsMatrix.rolesSaveError}</ToastTitle></Toast>, { intent: 'error' });
    } finally { setSavingRoles(false); }
  };
  const resetRoles = () => {
    if (matrix?.defaults) setDraft(Object.fromEntries(Object.entries(matrix.defaults).map(([r, ps]) => [r, [...(ps as Permission[])]])));
  };
  const [activeTab, setActiveTab] = useState<'matrix' | 'overrides'>('matrix');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userOverrides, setUserOverrides] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Lista de usuarios de la empresa (la misma de Usuarios); el filtro de texto la acota en cliente.
  const { data: tenantUsers = [] } = useQuery({
    queryKey: ['users-settings'],
    queryFn: fetchUsers,
    enabled: activeTab === 'overrides',
  });

  if (!canManageRoles) {
    return (
      <div className={s.page}>
        <Text className={s.noAccess}>{t.permissionsMatrix.noAccess}</Text>
      </div>
    );
  }

  const needle = searchQuery.trim().toLowerCase();
  const filteredUsers = tenantUsers.filter(u =>
    !needle ||
    (u.name ?? '').toLowerCase().includes(needle) ||
    u.phoneNumber.toLowerCase().includes(needle) ||
    u.roles.some(r => r.toLowerCase().includes(needle)),
  );

  const handleSelectUser = async (user: any) => {
    setLoading(true);
    try {
      const overrides = await getUserPermissionOverrides(user.phoneNumber);
      setSelectedUser(user);
      setUserOverrides(overrides.permissions);
    } catch (e: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>{t.permissionsMatrix.userNotFound}</ToastTitle>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (perm: string) => {
    setUserOverrides(prev =>
      prev.includes(perm)
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  const handleSaveOverrides = async () => {
    setSaving(true);
    try {
      await updateUserPermissionOverrides(selectedUser.phoneNumber, userOverrides);
      dispatchToast(
        <Toast>
          <ToastTitle>{t.permissionsMatrix.saved}</ToastTitle>
        </Toast>,
        { intent: 'success' }
      );
    } catch (e: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>{t.permissionsMatrix.saveError}</ToastTitle>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={s.page}>
      <div className={s.header}>
        <ShieldCheckmark24Regular />
        <PageTitle>{t.permissionsMatrix.title}</PageTitle>
      </div>
      <Text className={s.subtitle}>
        {t.permissionsMatrix.subtitle}
      </Text>

      {/* Tabs */}
      <div className={s.tabs}>
        <SubTabs selected={activeTab} onSelect={(v) => setActiveTab(v as typeof activeTab)} tabs={[{ value: 'matrix', label: t.permissionsMatrix.tabMatrix }, { value: 'overrides', label: t.permissionsMatrix.tabOverrides }]} />
      </div>

      {/* Tab 1: Matriz por rol */}
      {activeTab === 'matrix' && (
        <div>
          {isSuperAdmin && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <Text size={200} style={{ flex: '1 1 320px', color: tokens.colorNeutralForeground3 }}>{t.permissionsMatrix.roleEditHint}</Text>
              {draft && <Badge color="warning" appearance="tint">{t.permissionsMatrix.rolesDirty}</Badge>}
              <Button size="small" appearance="primary" disabled={!draft || savingRoles} onClick={saveRoles}>{t.permissionsMatrix.saveRoles}</Button>
              <Button size="small" disabled={!draft || savingRoles} onClick={() => setDraft(null)}>{t.permissionsMatrix.discardRoles}</Button>
              <Button size="small" disabled={savingRoles} onClick={resetRoles}>{t.permissionsMatrix.resetRoles}</Button>
            </div>
          )}
          {(Object.entries(PERMISSION_MODULES_KEYS) as unknown as [Module, Permission[]][]).map(([module, perms]) => (
            <div key={module} className={s.moduleSection}>
              <div className={s.moduleTitle}>{t.permissionModules[module]}</div>
              <div className={s.tableWrap}>
                <Table size="small">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell className={s.permCell}>{t.permissionsMatrix.permissionCol}</TableHeaderCell>
                      {ROLES.map(role => (
                        <TableHeaderCell key={role} className={s.headerCell}>
                          {t.settings.roles[role]}
                        </TableHeaderCell>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {perms.map(perm => (
                      <TableRow key={perm}>
                        <TableCell className={s.permCell}>
                          <Badge appearance="outline" size="small" color="informative" style={{ marginRight: 6 }}>
                            {perm}
                          </Badge>
                          {(t.permissionsMatrix.permissions as Record<string, string>)[perm] || perm}
                        </TableCell>
                        {ROLES.map(role => {
                          const has = (rolePermissions[role] ?? []).includes(perm as Permission);
                          return (
                            <TableCell
                              key={role}
                              className={s.checkCell}
                              onClick={() => toggleCell(role, perm as Permission)}
                              style={canEditCell(role, perm) ? { cursor: 'pointer', background: draft ? tokens.colorNeutralBackground2 : undefined } : undefined}
                            >
                              {has
                                ? <span className={s.allowed} title={t.permissionsMatrix.allowed}>✓</span>
                                : <span className={s.denied} title={t.permissionsMatrix.denied}>—</span>
                              }
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab 2: Overrides por usuario */}
      {activeTab === 'overrides' && (
        <div>
          <div className={s.overrideSearchWrap}>
            <Input
              placeholder={t.permissionsMatrix.searchPlaceholder}
              value={searchQuery}
              onChange={(_, d) => setSearchQuery(d.value)}
              style={{ flex: 1 }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '12px 0' }}>
            {filteredUsers.map(u => (
              <Button
                key={u.phoneNumber}
                appearance={selectedUser?.phoneNumber === u.phoneNumber ? 'primary' : 'subtle'}
                style={{ justifyContent: 'flex-start' }}
                disabled={loading}
                onClick={() => handleSelectUser(u)}
              >
                {u.name || u.phoneNumber} · {u.roles.join(', ')}
              </Button>
            ))}
            {filteredUsers.length === 0 && (
              <Text style={{ color: tokens.colorNeutralForeground3 }}>{t.permissionsMatrix.userNotFound}</Text>
            )}
          </div>

          {selectedUser ? (
            <div style={{ padding: '16px', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: '8px' }}>
              <Text weight="semibold">{t.permissionsMatrix.userLabel}: {selectedUser.name || selectedUser.phoneNumber}</Text>
              <Text size={300} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                {t.permissionsMatrix.roleLabel}: {selectedUser.activeRole} • {t.permissionsMatrix.phoneLabel}: {selectedUser.phoneNumber}
              </Text>

              <Text style={{ marginTop: '16px', fontSize: '13px', fontWeight: '500' }}>
                {t.permissionsMatrix.rolePermsTitle} ({selectedUser.activeRole}):
              </Text>
              <div style={{ marginTop: '8px', fontSize: '12px', color: tokens.colorNeutralForeground2 }}>
                {rolePermissions[selectedUser.activeRole as string]?.map((p: Permission) => (
                  <Badge key={p} appearance="outline" size="small" style={{ marginRight: '6px', marginBottom: '4px' }}>
                    {p}
                  </Badge>
                )) || t.permissionsMatrix.noBasePerms}
              </div>

              <Text style={{ marginTop: '20px', fontSize: '13px', fontWeight: '500' }}>
                {t.permissionsMatrix.extraPermsTitle}:
              </Text>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: '12px',
                marginTop: '12px',
                padding: '12px',
                backgroundColor: tokens.colorNeutralBackground2,
                borderRadius: '6px',
              }}>
                {(Object.entries(PERMISSION_MODULES_KEYS) as unknown as [Module, Permission[]][]).flatMap(([_, perms]) => perms).map(perm => {
                  const hasInRole = rolePermissions[selectedUser.activeRole as string]?.includes(perm as Permission);
                  if (hasInRole) return null; // No mostrar permisos que ya tiene por rol
                  return (
                    <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={userOverrides.includes(perm)}
                        onChange={() => handleTogglePermission(perm)}
                        disabled={saving}
                      />
                      <span style={{ fontSize: '12px' }}>{perm}</span>
                    </label>
                  );
                })}
              </div>

              <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                <Button
                  appearance="primary"
                  onClick={handleSaveOverrides}
                  disabled={saving}
                  icon={saving ? <Spinner size="tiny" /> : <CheckmarkCircle24Regular />}
                >
                  {t.permissionsMatrix.saveChanges}
                </Button>
                <Button
                  appearance="subtle"
                  onClick={() => {
                    setSelectedUser(null);
                    setUserOverrides([]);
                    setSearchQuery('');
                  }}
                >
                  {t.common.cancel}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: tokens.colorNeutralForeground3, padding: '32px' }}>
              {t.permissionsMatrix.searchPrompt}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
